import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Search, Menu, X, LogOut, LogIn, LayoutDashboard, Monitor, Handshake, ChevronDown, Globe, Moon, Sun, Home, Network, Megaphone, Star, CalendarDays, HelpCircle, Mail, MoreHorizontal, Brain, User, Shield, RefreshCw, type LucideIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAffiliateCheck } from "@/hooks/useAffiliateCheck";
import { useMediaOwnerCheck } from "@/hooks/useMediaOwnerCheck";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { NotificationsPopover } from "@/components/notifications/NotificationsPopover";
import { GlobalSearchDialog } from "@/components/search/GlobalSearchDialog";
import { useNotifications } from "@/hooks/useNotifications";
import logoUrl from "@/assets/fusion-logo-new.png";

type NavLinkDef = {
  label: string;
  labelAr?: string;
  path: string;
  icon: LucideIcon;
  desc?: string;
  descAr?: string;
};

const PRIMARY_LINKS: NavLinkDef[] = [
  { label: "Home", labelAr: "الرئيسية", path: "/", icon: Home, desc: "Platform overview & highlights", descAr: "نظرة عامة وأبرز المزايا" },
  { label: "Create Campaign", labelAr: "إنشاء حملة", path: "/campaign", icon: Megaphone, desc: "Launch a new DOOH campaign", descAr: "أطلق حملة إعلانية جديدة" },
  { label: "AI Campaign Creator", labelAr: "منشئ الحملات بالذكاء الاصطناعي", path: "/ai-campaign-creator", icon: Brain, desc: "Plan with AI in seconds", descAr: "خطّط بالذكاء الاصطناعي في ثوانٍ" },
  { label: "Become an Affiliate", labelAr: "كن شريكًا", path: "/affiliate-program", icon: Handshake, desc: "Earn by referring clients", descAr: "اربح عبر إحالة العملاء" },
  { label: "Influencers", labelAr: "المؤثرون", path: "/influencers", icon: Star, desc: "Book Saudi creators", descAr: "احجز مبدعين سعوديين" },
  { label: "Events", labelAr: "الفعاليات", path: "/events-sponsors", icon: CalendarDays, desc: "Sponsor or list events", descAr: "ارعَ أو أدرج الفعاليات" },
  { label: "Network", labelAr: "الشبكة", path: "/partners", icon: Network, desc: "Our media partners", descAr: "شركاؤنا الإعلاميون" },
];

const MORE_LINKS: NavLinkDef[] = [
  { label: "Who We Are", labelAr: "من نحن", path: "/who-we-are", icon: User, desc: "About Fusion Media", descAr: "عن فيوجن ميديا" },
  { label: "Contact", labelAr: "تواصل", path: "/contact", icon: Mail, desc: "Talk to our team", descAr: "تحدث مع فريقنا" },
  { label: "Help & FAQs", labelAr: "المساعدة", path: "/faqs", icon: HelpCircle, desc: "Answers to common questions", descAr: "إجابات للأسئلة الشائعة" },
  { label: "Privacy Policy", labelAr: "سياسة الخصوصية", path: "/privacy-policy", icon: Shield, desc: "How we handle your data", descAr: "كيفية تعاملنا مع بياناتك" },
  { label: "Return Policy", labelAr: "سياسة الاسترداد", path: "/return-policy", icon: RefreshCw, desc: "Refunds & cancellations", descAr: "الاستردادات والإلغاءات" },
];


export const TopNavigation = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { isAffiliate } = useAffiliateCheck();
  const { isMediaOwner } = useMediaOwnerCheck();
  const isMobile = useIsMobile();
  const { resolvedTheme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const navRef = useRef<HTMLElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(PRIMARY_LINKS.length);

  // Measure how many primary links fit in the available nav width and overflow the rest into "More".
  useLayoutEffect(() => {
    if (isMobile) return;
    const recompute = () => {
      const nav = navRef.current;
      const measure = measureRef.current;
      if (!nav || !measure) return;
      const available = nav.clientWidth;
      const items = Array.from(measure.querySelectorAll<HTMLElement>("[data-measure-item]"));
      const moreEl = measure.querySelector<HTMLElement>("[data-measure-more]");
      const gap = 4; // matches gap-1
      const moreW = (moreEl?.offsetWidth ?? 0) + gap;
      let used = 0;
      let count = 0;
      for (let i = 0; i < items.length; i++) {
        const w = items[i].offsetWidth + gap;
        // reserve space for More if there are still remaining items after this one
        const reserve = i < items.length - 1 ? moreW : 0;
        if (used + w + reserve <= available) {
          used += w;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (navRef.current) ro.observe(navRef.current);
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [isMobile, language, user]);

  const visibleLinks = useMemo(() => PRIMARY_LINKS.slice(0, visibleCount), [visibleCount]);
  const overflowLinks = useMemo(() => PRIMARY_LINKS.slice(visibleCount), [visibleCount]);
  const combinedMoreLinks = useMemo(() => [...overflowLinks, ...MORE_LINKS], [overflowLinks]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (path: string) => {
    navigate(path);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isActive = (path: string) =>
    path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(path + "/");

  const dashboardPath = isMediaOwner
    ? "/media-owner-dashboard"
    : isAffiliate
    ? "/affiliate-dashboard"
    : "/client-dashboard";

  const dashboardLabel = isMediaOwner
    ? language === "ar" ? "لوحة مالك الوسائط" : "Media Owner Dashboard"
    : isAffiliate
    ? language === "ar" ? "لوحة الشريك" : "Affiliate Dashboard"
    : language === "ar" ? "لوحة العميل" : "Dashboard";

  return (
    <>
    <header
      data-tour="top-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-border/60 shadow-sm"
          : "bg-background/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="w-full px-3 sm:px-4 md:pl-0 md:pr-4 h-20 md:h-[104px] flex items-center gap-3 md:gap-4">
        {/* Logo */}
        <div className="relative flex items-center min-w-0 shrink md:-ml-6 md:shrink-0 mt-1 md:mt-6">
          <img
            src={logoUrl}
            alt="Fusion Media"
            className="h-36 sm:h-24 md:h-72 w-auto object-contain pointer-events-none select-none"
          />
          {/* Clickable hit-area aligned to the visible logo pixels only */}
          <button
            type="button"
            onClick={() => go("/")}
            aria-label="Fusion Media home"
            className="absolute bg-transparent border-0 p-0 m-0 cursor-pointer hover:opacity-90 transition-opacity"
            style={{
              left: "16.9%",
              width: "67.2%",
              top: "33.9%",
              height: "24.1%",
            }}
          />
        </div>


        {/* Center nav (desktop) */}
        {!isMobile && (
          <nav ref={navRef} data-tour="nav-primary" className="flex-1 flex items-center justify-center gap-1 min-w-0 overflow-hidden">
            {visibleLinks.map((link) => {
              const active = isActive(link.path);
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => go(link.path)}
                  className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    active
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
                  }`}
                >
                  <Icon className="h-[18px] w-[18px] text-primary shrink-0" />
                  {language === "ar" && link.labelAr ? link.labelAr : link.label}
                  {active && (
                    <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-tour="nav-more"
                  className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    combinedMoreLinks.some((l) => isActive(l.path))
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
                  }`}
                >
                  <MoreHorizontal className="h-4 w-4 text-primary" />
                  {language === "ar" ? "المزيد" : "More"}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={12}
                className={`z-[9999] p-0 rounded-2xl border-border/70 shadow-2xl shadow-primary/10 overflow-hidden ${
                  overflowLinks.length > 0 ? "w-[640px]" : "w-[360px]"
                }`}
              >
                <div className={`grid ${overflowLinks.length > 0 ? "grid-cols-2" : "grid-cols-1"} bg-background`}>
                  {/* Explore (overflow primary links) */}
                  {overflowLinks.length > 0 && (
                    <div className="p-4 border-r border-border/60">
                      <div className="px-2 pb-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-primary/80">
                        {language === "ar" ? "استكشف" : "Explore"}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {overflowLinks.map((link) => {
                          const Icon = link.icon;
                          const active = isActive(link.path);
                          return (
                            <button
                              key={link.path}
                              onClick={() => go(link.path)}
                              className={`group flex items-start gap-3 p-2.5 rounded-xl text-left transition-colors ${
                                active ? "bg-primary/10" : "hover:bg-muted/70"
                              }`}
                            >
                              <span className="mt-0.5 h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center shrink-0 ring-1 ring-primary/15 group-hover:ring-primary/30 transition">
                                <Icon className="h-[18px] w-[18px] text-primary" />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className={`block text-sm font-semibold leading-tight ${active ? "text-primary" : "text-foreground"}`}>
                                  {language === "ar" && link.labelAr ? link.labelAr : link.label}
                                </span>
                                {link.desc && (
                                  <span className="block mt-0.5 text-xs text-muted-foreground leading-snug line-clamp-2">
                                    {language === "ar" && link.descAr ? link.descAr : link.desc}
                                  </span>
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Resources (static MORE_LINKS) */}
                  <div className="p-4">
                    <div className="px-2 pb-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-primary/80">
                      {language === "ar" ? "موارد" : "Resources"}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {MORE_LINKS.map((link) => {
                        const Icon = link.icon;
                        const active = isActive(link.path);
                        return (
                          <button
                            key={link.path}
                            onClick={() => go(link.path)}
                            className={`group flex items-start gap-3 p-2.5 rounded-xl text-left transition-colors ${
                              active ? "bg-primary/10" : "hover:bg-muted/70"
                            }`}
                          >
                            <span className="mt-0.5 h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center shrink-0 ring-1 ring-primary/15 group-hover:ring-primary/30 transition">
                              <Icon className="h-[18px] w-[18px] text-primary" />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className={`block text-sm font-semibold leading-tight ${active ? "text-primary" : "text-foreground"}`}>
                                {language === "ar" && link.labelAr ? link.labelAr : link.label}
                              </span>
                              {link.desc && (
                                <span className="block mt-0.5 text-xs text-muted-foreground leading-snug line-clamp-2">
                                  {language === "ar" && link.descAr ? link.descAr : link.desc}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer CTA strip */}
                <div className="border-t border-border/60 bg-muted/30 px-4 py-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-muted-foreground">
                    {language === "ar" ? "هل أنت مستعد للإطلاق؟" : "Ready to launch a campaign?"}
                  </div>
                  <button
                    onClick={() => go(user ? dashboardPath : "/campaign")}
                    className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
                  >
                    {language === "ar" ? "ابدأ الآن" : "Get started"}
                    <ChevronDown className="h-3 w-3 -rotate-90" />
                  </button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Hidden measurement row — used to compute how many links fit */}
            <div
              ref={measureRef}
              aria-hidden="true"
              className="absolute -top-[9999px] left-0 flex items-center gap-1 pointer-events-none invisible"
            >
              {PRIMARY_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <span
                    key={link.path}
                    data-measure-item
                    className="relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap"
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {language === "ar" && link.labelAr ? link.labelAr : link.label}
                  </span>
                );
              })}
              <span
                data-measure-more
                className="relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap"
              >
                <MoreHorizontal className="h-4 w-4" />
                {language === "ar" ? "المزيد" : "More"}
                <ChevronDown className="h-3.5 w-3.5" />
              </span>
            </div>
          </nav>
        )}

        

        {/* Right cluster */}
        <div className="flex items-center gap-1.5 shrink-0 ml-auto">
          {!isMobile && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
              <NotificationsPopover
                trigger={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full relative"
                    aria-label="Notifications"
                  >
                    <Bell className="h-4 w-4" />
                    {user && unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shadow-sm">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                }
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => setLanguage(language === "en" ? "ar" : "en")}
                aria-label="Toggle language"
                title={language === "en" ? "العربية" : "English"}
              >
                <Globe className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* Avatar / Auth menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-tour="nav-account" className="flex items-center gap-2 ml-1 px-2 py-1.5 rounded-full hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md shadow-primary/30">
                  {user?.email?.[0]?.toUpperCase() ?? "G"}
                </div>
                {!isMobile && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 z-[9999]">
              {user ? (
                <>
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => go(dashboardPath)}>
                    {isMediaOwner ? <Monitor className="h-4 w-4 mr-2" /> : isAffiliate ? <Handshake className="h-4 w-4 mr-2" /> : <LayoutDashboard className="h-4 w-4 mr-2" />}
                    {dashboardLabel}
                  </DropdownMenuItem>
                  {!isMediaOwner && !isAffiliate && (
                    <DropdownMenuItem onSelect={() => go("/affiliate-program")}>
                      <Handshake className="h-4 w-4 mr-2" />
                      {language === "ar" ? "كن شريك إحالة" : "Become an Affiliate"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={async () => { await signOut(); navigate("/"); }} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("logout") || "Sign out"}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onSelect={() => go("/auth")}>
                    <LogIn className="h-4 w-4 mr-2" />
                    {t("login") || "Sign in"}
                  </DropdownMenuItem>
                </>
              )}
              {isMobile && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
                    {resolvedTheme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                    {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setLanguage(language === "en" ? "ar" : "en")}>
                    <Globe className="h-4 w-4 mr-2" />
                    {language === "en" ? "العربية" : "English"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Dashboard CTA (desktop, signed-in) */}
          {!isMobile && user && (
            <Button
              data-tour="nav-cta"
              onClick={() => go(dashboardPath)}
              className="ml-2 h-9 rounded-full bg-gradient-to-r from-primary to-primary-light text-primary-foreground font-semibold shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              {language === "ar" ? "لوحة التحكم" : "Dashboard"}
            </Button>
          )}
          {!isMobile && !user && (
            <Button
              data-tour="nav-cta"
              onClick={() => go("/auth")}
              className="ml-2 h-9 rounded-full bg-gradient-to-r from-primary to-primary-light text-primary-foreground font-semibold shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-0.5 transition-all"
            >
              {language === "ar" ? "ابدأ الآن" : "Get Started"}
            </Button>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          )}
        </div>
      </div>
    </header>

    {/* Mobile menu sheet — rendered as sibling to header to avoid stacking-context traps */}
    {isMobile && open && (
      <div className="fixed inset-x-0 top-20 bottom-0 z-[70] bg-background overflow-y-auto animate-fade-in shadow-2xl">
        <nav className="px-4 py-6 flex flex-col gap-1 pb-24">
          {[...PRIMARY_LINKS, ...MORE_LINKS].map((link) => {
            const active = isActive(link.path);
            const Icon = link.icon;
            return (
              <button
                key={link.path}
                onClick={() => go(link.path)}
                className={`flex items-center gap-3 text-start px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted/60"
                }`}
              >
                <Icon className="h-5 w-5 text-primary shrink-0" />
                {language === "ar" && link.labelAr ? link.labelAr : link.label}
              </button>
            );
          })}
          <div className="h-px bg-border my-4" />
          {user ? (
            <Button
              onClick={() => go(dashboardPath)}
              className="h-12 rounded-xl bg-gradient-to-r from-primary to-primary-light text-primary-foreground font-semibold"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              {dashboardLabel}
            </Button>
          ) : (
            <Button
              onClick={() => go("/auth")}
              className="h-12 rounded-xl bg-gradient-to-r from-primary to-primary-light text-primary-foreground font-semibold"
            >
              {language === "ar" ? "ابدأ الآن" : "Get Started"}
            </Button>
          )}
        </nav>
      </div>
    )}
    <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};
